// --- utils

// --- internal
import { useClientAddresses, useClientAddress } from "../address";
import {
  useSchemaDefinitions as useAddressSchema,
  useUischemaDefinitions as useAddressUischema
} from "../address/schemas";

import { useClientEmails, useClientEmail } from "../email";
import {
  useSchema as useEmailSchema,
  useUischema as useEmailUischema
} from "../email/schemas";

import { useClientPhones, useClientPhone } from "../phone";
import {
  useSchema as usePhoneSchema,
  useUischema as usePhoneUischema
} from "../phone/schemas";

// --- types
import type { CompanyContext } from "./types";
import type { JsonSchema7, UISchemaElement } from "@jsonforms/core";

export const useSchema = ({
  countries,
  country,
  regions,
  baseModel,
  config
}: Partial<CompanyContext>): JsonSchema7 => {
  const schema: JsonSchema7 = {
    type: "object",
    title: "Company",
    required: ["name"],
    definitions: useAddressSchema({
      config,
      countries,
      regions,
      baseModel
    }),
    properties: {
      id: {
        type: ["string", "null"],
        title: "ID",
        description: "The AutoGenerated ID of this Company.",
        readOnly: true
      },
      name: {
        type: "string",
        title: "Company Name"
      },
      regNumber: {
        type: ["string", "null"],
        title: "Company Number"
      },
      tax: {
        type: "object",
        title: "Tax Details",
        properties: {
          number: {
            type: ["string", "null"],
            title: "Registered Tax ID"
          }
        }
      },
      addressId: {
        type: ["string", "null"],
        title: "Address",
        default: baseModel?.addressId
      },
      address: { $ref: "#/definitions/address" }
    }
  };

  // If we are we already have an address, then we will always use the addressId
  // otherwise we will allow a full address object as we need to create one
  if (baseModel?.addressId) {
    schema.required!.push("addressId");
    delete schema?.properties?.address;
  } else {
    schema.required!.push("address");
  }

  // TODO: same logic as address
  // if (baseModel?.phoneId) {
  //   delete schema?.properties?.phone;
  // }

  // TODO: same logic as address
  // if (baseModel?.emailId) {
  //   delete schema?.properties?.email;
  // }

  return schema;
};

export const useUischema = ({
  baseModel,
  minimal,
  countries,
  regions
}: Partial<CompanyContext>) => {
  const uiSchema = {
    type: "VerticalLayout",
    elements: [
      {
        type: "Control",
        scope: "#/properties/name",
        i18n: "form.companyName",
        options: {
          autoFocus: true,
          autocomplete: "off",
          placeholder: "My company name"
        }
      },
      {
        type: "HorizontalLayout",
        elements: [
          {
            type: "Control",
            scope: "#/properties/regNumber",
            i18n: "form.companyNumber",
            options: {
              placeholder: "Registered tax or GST"
            }
          },
          {
            type: "Control",
            scope: "#/properties/tax/properties/number",
            i18n: "form.taxNumber",
            options: {
              placeholder: "Tax Id"
            }
          }
        ]
      }
    ]
  };

  // this is where we conditionally render either an existing addressId or a new address uiSchema
  if (!baseModel?.addressId) {
    uiSchema.elements.push({
      type: "VerticalLayout",
      elements: [useAddressUischema({ countries, regions })]
    } as any);
  } else {
    uiSchema.elements.push({
      type: "Manager",
      scope: "#/properties/addressId",
      i18nKey: "form.address",
      options: {
        manage: {
          useList: useClientAddresses,
          useMutate: useClientAddress
        }
      }
    } as any);
  }

  // TODO: the allOF/anyOf schema needs to be handled in the uischema as well
  if (!minimal) {
    // this is where we conditionally render either an existing phoneId or a new phone uiSchema
    // if (!baseModel?.phoneId) {
    //   uiSchema.elements.push({
    //     type: "VerticalLayout",
    //     elements: [
    //       {
    //         type: "Control",
    //         scope: "#/properties/phone",
    //         options: {
    //           autoFocus: true,
    //           autocomplete: "off",
    //           detail: usePhoneUischema(),
    //         },
    //       },
    //     ],
    //   } as any);
    // } else {
    //   uiSchema.elements.push({
    //     type: "Manager",
    //     scope: "#/properties/phoneId",
    //     options: {
    //       i18nKey: "client.phone",
    //       manage: {
    //         useList: useClientPhones,
    //         useMutate: useClientPhone,
    //       },
    //     },
    //   } as any);
    // }
    // // this is where we conditionally render either an existing emailId or a new email uiSchema
    // if (!baseModel?.emailId) {
    //   uiSchema.elements.push({
    //     type: "VerticalLayout",
    //     elements: [
    //       {
    //         type: "Control",
    //         scope: "#/properties/email",
    //         options: {
    //           autoFocus: true,
    //           autocomplete: "off",
    //           detail: useEmailUischema(),
    //         },
    //       },
    //     ],
    //   } as any);
    // } else {
    //   uiSchema.elements.push({
    //     type: "Manager",
    //     scope: "#/properties/emailId",
    //     options: {
    //       i18nKey: "client.email",
    //       manage: {
    //         useList: useClientEmails,
    //         useMutate: useClientEmail,
    //       },
    //     },
    //   } as any);
    // }
  }

  return uiSchema as UISchemaElement;
};
